import { action, computed, makeObservable, observable } from 'mobx'

import InfoSectionId from '~/client/src/shared/enums/InfoSectionId'
import { TagIconType } from '~/client/src/shared/enums/TagIcon'
import UserFieldId from '~/client/src/shared/enums/UserFieldId'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { ITag } from '~/client/src/shared/models/Tag'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import { IAddEditDialogField } from '~/client/src/shared/stores/ui/AddEditItemDialog.store'
import BaseAddEditMemberDialogStore from '~/client/src/shared/stores/ui/BaseAddEditMemberDialog.store'
import { IServiceUserDto } from '~/client/src/shared/types/UserDto'
import { formatBulkSaveError } from '~/client/src/shared/utils/bulkSaveErrorFormatter'
import { wasPhoneCleared } from '~/client/src/shared/utils/phoneNumberHelpers'

import { FormFieldType } from '../../enums/FormFieldType'
import { TagType } from '../../enums/TagType'
import User from '../../models/User'
import ProjectMembersStore from '../../stores/domain/ProjectMembers.store'
import UserProjectsStore from '../../stores/domain/UserProjects.store'

interface IFormUserFields {
  id: string
  avatarUrl: string
  firstName: string
  lastName: string
  globalRole: string
  companyId: string
  teams: ITag[]
  roles: ITag[]
  email: string
  phoneNumber: string
  trades: ITag[]
}

const requiredFieldIds = [
  UserFieldId.FirstName,
  UserFieldId.LastName,
  UserFieldId.Company,
  UserFieldId.AccountPosition,
]

export default class CompactUserProfileStore extends BaseAddEditMemberDialogStore<IFormUserFields> {
  @observable public isEditing: boolean = false
  @observable private user: User = null
  // STX1-637: itemsToEdit is the LIVE User instance and base submit() mutates it
  // before updateItems runs, so the loaded phone must be captured at edit start.
  @observable private phoneBeforeEdit: string = ''
  // Strand-confirmation message when clearing the phone would cost sign-in.
  @observable public phoneStrandPrompt: string = null

  public constructor(
    private readonly eventsStore: EventsStore,
    private readonly projectMembersStore: ProjectMembersStore,
    userProjectsStore: UserProjectsStore,
    user: User,
    allUsers: User[],
  ) {
    super(userProjectsStore, [user], allUsers)
    this.setUser(user)
    this.recreateFormStores()
    this.updateFormStores()
    makeObservable(this)
  }

  public setUser(newUser: User = null) {
    this.user = newUser
  }

  public get state() {
    return this.eventsStore.appState
  }

  @computed
  public get fields(): IAddEditDialogField[] {
    const fields: IAddEditDialogField[] = [
      {
        id: UserFieldId.AvatarUrl,
        label: Localization.translator.avatar,
        value: this.avatarUrlFieldValue,
        sectionId: InfoSectionId.Personal,
        type: FormFieldType.Avatar,
        onChange: this.handleAvatarChange,
      },
      {
        id: UserFieldId.FirstName,
        label: Localization.translator.firstName,
        value: this.getFieldValueById(UserFieldId.FirstName),
        sectionId: InfoSectionId.Personal,
        type: FormFieldType.Text,
        onChange: this.handleChange,
        onValueReset: this.handleValueReset.bind(this, UserFieldId.FirstName),
      },
      {
        id: UserFieldId.LastName,
        label: Localization.translator.lastName,
        value: this.getFieldValueById(UserFieldId.LastName),
        sectionId: InfoSectionId.Personal,
        type: FormFieldType.Text,
        onChange: this.handleChange,
        onValueReset: this.handleValueReset.bind(this, UserFieldId.LastName),
      },
      {
        id: UserFieldId.GlobalRole,
        label: Localization.translator.globalRole,
        value: this.getFieldValueById(UserFieldId.GlobalRole),
        sectionId: InfoSectionId.Personal,
        type: FormFieldType.TextAsTag,
        serviceInfo: TagIconType.Role,
        onChange: this.handleTagChange.bind(null, UserFieldId.GlobalRole),
        onValueReset: this.handleValueReset.bind(this, UserFieldId.GlobalRole),
      },
      {
        id: UserFieldId.PhoneNumber,
        label: Localization.translator.phoneNumber,
        value: this.getFieldValueById(UserFieldId.PhoneNumber),
        sectionId: InfoSectionId.Personal,
        type: FormFieldType.Phone,
        onChange: this.handlePhoneNumberChange.bind(
          null,
          UserFieldId.PhoneNumber,
        ),
      },
      {
        id: UserFieldId.Email,
        label: Localization.translator.email_noun,
        value: this.getFieldValueById(UserFieldId.Email),
        sectionId: InfoSectionId.Personal,
        type: FormFieldType.Text,
        onChange: this.handleChange,
        onValueReset: this.handleValueReset.bind(this, UserFieldId.Email),
      },
      {
        id: UserFieldId.Company,
        label: Localization.translator.company,
        value: this.getFieldValueById(UserFieldId.Company),
        sectionId: InfoSectionId.Company,
        type: FormFieldType.Tag,
        serviceInfo: TagType.Company,
        onChange: this.handleTagChange.bind(null, UserFieldId.Company),
        isItemCreationAllowed: this.isAdmin,
      },
      {
        id: UserFieldId.Teams,
        label: Localization.translator.teams,
        value: this.getFieldValueById(UserFieldId.Teams),
        sectionId: InfoSectionId.Project,
        type: FormFieldType.Tags,
        serviceInfo: TagType.Team,
        onChange: this.handleTagsChange.bind(null, UserFieldId.Teams),
        isItemCreationAllowed: true,
      },
      {
        id: UserFieldId.DefaultTeams,
        label: Localization.translator.defaultTeams,
        value: this.getFieldValueById(UserFieldId.DefaultTeams),
        sectionId: InfoSectionId.Project,
        type: FormFieldType.Tags,
        serviceInfo: TagType.DefaultTeam,
        onChange: this.handleTagsChange.bind(null, UserFieldId.DefaultTeams),
      },
      {
        id: UserFieldId.Trades,
        label: Localization.translator.projectTrades,
        value: this.getFieldValueById(UserFieldId.Trades),
        sectionId: InfoSectionId.Project,
        type: FormFieldType.Tags,
        serviceInfo: TagType.Trade,
        onChange: this.handleTagsChange.bind(null, UserFieldId.Trades),
        isItemCreationAllowed: true,
      },
    ]

    if (this.isEditing) {
      const { requiredFields } = this.activeFormStore
      fields.forEach(field => {
        field.isValid = this.isFieldValid(field)
        field.isRequired = requiredFields && requiredFields.includes(field.id)
      })
    }

    return fields
  }

  public get isCurrentUser() {
    return this.currentUser.id === this.user.id
  }

  public get isAdmin() {
    return this.state.userActiveProjectSettings?.isAdmin
  }

  public get isEditAllowed() {
    return (this.isCurrentUser || this.isAdmin) && !!this.user
  }

  public get isScanMaster(): boolean {
    const { userActiveProjectSettings } = this.eventsStore.appState

    return (
      (userActiveProjectSettings?.isAdmin ||
        userActiveProjectSettings?.isScanMaster) &&
      !!this.user
    )
  }

  private get currentUser() {
    return this.state.user
  }

  @action.bound
  public cancelEditing() {
    this.isEditing = false
    this.recreateFormStores()
    this.updateFormStores()
  }

  @action.bound
  public startEditing() {
    this.phoneBeforeEdit = this.user.phoneNumber
    this.isEditing = true
  }

  protected updateFormStoreItem(item: User) {
    const userProject = this.userProjectsStore.getByUser(item)

    Object.assign(item, {
      [UserFieldId.Company]: userProject?.companyId || '',
      // No Project Roles field, but save rebuilds roles from the form, so the
      // loaded roles must ride along unchanged.
      [UserFieldId.Roles]: userProject?.roles || [],
      [UserFieldId.Trades]: userProject?.trades || [],
      [UserFieldId.Teams]: userProject?.teams || [],
      [UserFieldId.DefaultTeams]: userProject?.defaultTeams || [],
      [UserFieldId.Tags]: userProject?.tags || [],
    })
  }

  protected getRequiredFields(): string[] {
    return this.isCurrentUser
      ? [...requiredFieldIds, UserFieldId.Email]
      : requiredFieldIds
  }

  protected async updateItems(items: IServiceUserDto[]) {
    try {
      await this.projectMembersStore.saveMembers(items)
      this.isEditing = false
    } catch (e) {
      console.warn(e)
      alert(formatBulkSaveError(e))
      // Save failed → skip the phone removal.
      return
    }

    // STX1-637: emptying the phone removes it everywhere (central + Firebase +
    // tenants). saveMembers only preserves a blank, so dispatch the removal.
    if (wasPhoneCleared(this.phoneBeforeEdit, items[0]?.phoneNumber)) {
      const result = await this.projectMembersStore.removeMemberPhone(
        this.user.id,
      )
      if (result.outcome === 'confirm-strand') {
        this.phoneStrandPrompt = result.message
      } else if (result.outcome === 'error') {
        alert(result.message)
      }
    }
  }

  @action.bound
  public async confirmPhoneClear() {
    const message = this.phoneStrandPrompt
    this.phoneStrandPrompt = null
    if (message === null) {
      return
    }

    const result = await this.projectMembersStore.removeMemberPhone(
      this.user.id,
      true,
    )
    if (result.outcome === 'error') {
      alert(result.message)
    }
  }

  @action.bound
  public cancelPhoneClear() {
    this.phoneStrandPrompt = null
    // Keep the number: submit() had emptied the live user, so restore it.
    this.user.phoneNumber = this.phoneBeforeEdit
  }

  protected createFormItemFields(): IFormUserFields {
    return {
      id: null,
      [UserFieldId.AvatarUrl]: '',
      firstName: '',
      lastName: '',
      [UserFieldId.GlobalRole]: '',
      [UserFieldId.Company]: '',
      teams: [],
      [UserFieldId.Roles]: [],
      [UserFieldId.Trades]: [],
      email: '',
      phoneNumber: '',
    }
  }
}
